import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Loader2, CheckCircle, AlertCircle, ArrowLeft, Send } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PRODUCTS_OPTIONS = [
  { id: 'football', label: 'Football' },
  { id: 'baseball', label: 'Baseball' },
  { id: 'basketball', label: 'Basketball' },
  { id: 'pokemon', label: 'Pokemon' },
  { id: 'one_piece', label: 'One Piece' },
  { id: 'other_tcg', label: 'Other TCG' },
  { id: 'non_sports', label: 'Non-Sports' },
  { id: 'other', label: 'Other' }
];

const CHANNELS_OPTIONS = [
  { id: 'whatnot', label: 'WhatNot' },
  { id: 'ebay', label: 'eBay' },
  { id: 'live_shows', label: 'Live Card Shows' },
  { id: 'social_media', label: 'Social Media' },
  { id: 'other', label: 'Other' }
];

export default function DealerApplyPage() {
  const { user, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    products_sold: [],
    products_sold_other: '',
    sales_channels: [],
    sales_channels_other: '',
    business_goals: '',
    agreed_to_terms: false
  });
  
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [existingApplication, setExistingApplication] = useState(null);
  const [dealerStatus, setDealerStatus] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      sessionStorage.setItem('redirectAfterLogin', '/dealer/apply');
      navigate('/login');
      return;
    }
    
    // Pre-fill form with user data
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || ''
      }));
    }
    
    // Check existing application status
    checkDealerStatus();
  }, [user, isAuthenticated, navigate]);

  const checkDealerStatus = async () => {
    try {
      const response = await axios.get(`${API}/api/dealer/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDealerStatus(response.data);
      
      if (response.data.application) {
        setExistingApplication(response.data.application);
      }
    } catch (error) {
      console.error('Failed to check dealer status:', error);
    } finally {
      setChecking(false);
    }
  };

  const handleProductChange = (productId, checked) => {
    setFormData(prev => ({
      ...prev,
      products_sold: checked 
        ? [...prev.products_sold, productId]
        : prev.products_sold.filter(p => p !== productId)
    }));
  };

  const handleChannelChange = (channelId, checked) => {
    setFormData(prev => ({
      ...prev,
      sales_channels: checked
        ? [...prev.sales_channels, channelId]
        : prev.sales_channels.filter(c => c !== channelId)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // Validation
    if (!formData.name || !formData.email || !formData.phone) {
      setError('Please fill in all contact information');
      setLoading(false);
      return;
    }

    if (formData.products_sold.length === 0) {
      setError('Please select at least one product type you sell');
      setLoading(false);
      return;
    }

    if (formData.sales_channels.length === 0) {
      setError('Please select at least one sales channel');
      setLoading(false);
      return;
    }

    if (!formData.business_goals.trim()) {
      setError('Please tell us about your business goals');
      setLoading(false);
      return;
    }

    if (!formData.agreed_to_terms) {
      setError('You must agree to the Dealer Terms & Conditions');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/api/dealer/apply`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess(true);
      setExistingApplication(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
        <Footer />
      </div>
    );
  }

  // Already approved dealer
  if (dealerStatus?.dealer_status === 'approved') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-16">
          <Card className="shadow-lg">
            <CardContent className="p-8 text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">You&apos;re Already a Dealer!</h2>
              <p className="text-gray-600 mb-6">
                Your dealer account is active. You can start submitting cards at dealer pricing.
              </p>
              <div className="flex gap-4 justify-center">
                <Button onClick={() => navigate('/dealer/dashboard')}>
                  View Dealer Dashboard
                </Button>
                <Button variant="outline" onClick={() => navigate('/submission/new')}>
                  New Submission
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  // Pending application
  if (dealerStatus?.dealer_status === 'pending') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-16">
          <Card className="shadow-lg">
            <CardContent className="p-8 text-center">
              <Loader2 className="h-16 w-16 text-blue-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Application Under Review</h2>
              <p className="text-gray-600 mb-4">
                Your dealer application is being reviewed. We will notify you by email once a decision is made.
              </p>
              <Badge variant="outline" className="text-blue-600 border-blue-600">
                Submitted: {new Date(existingApplication?.created_at).toLocaleDateString()}
              </Badge>
              <div className="mt-6">
                <Button variant="outline" onClick={() => navigate('/dashboard')}>
                  Back to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  // Rejected - check if can reapply
  if (dealerStatus?.dealer_status === 'rejected' && !dealerStatus?.can_reapply) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-16">
          <Card className="shadow-lg">
            <CardContent className="p-8 text-center">
              <AlertCircle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Application Previously Declined</h2>
              <p className="text-gray-600 mb-4">
                Your previous application was not approved.
              </p>
              {dealerStatus?.can_reapply_at && (
                <p className="text-gray-500 mb-4">
                  You can reapply after: <strong>{new Date(dealerStatus.can_reapply_at).toLocaleDateString()}</strong>
                </p>
              )}
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-16">
          <Card className="shadow-lg">
            <CardContent className="p-8 text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Application Submitted!</h2>
              <p className="text-gray-600 mb-6">
                Thank you for applying to the NICE Dealer Program. We will review your application 
                and notify you by email within 2-3 business days.
              </p>
              <Button onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/dealer-program')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dealer Program
        </Button>

        <Card className="shadow-lg">
          <CardHeader className="border-b bg-gray-50">
            <CardTitle className="text-2xl">Dealer Application</CardTitle>
            <CardDescription>
              Fill out the form below to apply for the NICE Dealer Program
            </CardDescription>
          </CardHeader>
          
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2">Contact Information</h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="John Smith"
                      data-testid="dealer-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="john@example.com"
                      data-testid="dealer-email-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="(555) 123-4567"
                    data-testid="dealer-phone-input"
                  />
                </div>
              </div>

              {/* Products Sold */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2">What do you sell? *</h3>
                <p className="text-sm text-gray-500">Select all that apply</p>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {PRODUCTS_OPTIONS.map((product) => (
                    <div key={product.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`product-${product.id}`}
                        checked={formData.products_sold.includes(product.id)}
                        onCheckedChange={(checked) => handleProductChange(product.id, checked)}
                        data-testid={`product-${product.id}-checkbox`}
                      />
                      <Label htmlFor={`product-${product.id}`} className="text-sm cursor-pointer">
                        {product.label}
                      </Label>
                    </div>
                  ))}
                </div>
                
                {formData.products_sold.includes('other') && (
                  <div>
                    <Label htmlFor="products_other">Please specify other products</Label>
                    <Input
                      id="products_other"
                      value={formData.products_sold_other}
                      onChange={(e) => setFormData({...formData, products_sold_other: e.target.value})}
                      placeholder="e.g., Vintage cards, Memorabilia"
                      data-testid="products-other-input"
                    />
                  </div>
                )}
              </div>

              {/* Sales Channels */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2">Where do you sell? *</h3>
                <p className="text-sm text-gray-500">Select all that apply</p>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {CHANNELS_OPTIONS.map((channel) => (
                    <div key={channel.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`channel-${channel.id}`}
                        checked={formData.sales_channels.includes(channel.id)}
                        onCheckedChange={(checked) => handleChannelChange(channel.id, checked)}
                        data-testid={`channel-${channel.id}-checkbox`}
                      />
                      <Label htmlFor={`channel-${channel.id}`} className="text-sm cursor-pointer">
                        {channel.label}
                      </Label>
                    </div>
                  ))}
                </div>
                
                {formData.sales_channels.includes('other') && (
                  <div>
                    <Label htmlFor="channels_other">Please specify other channels</Label>
                    <Input
                      id="channels_other"
                      value={formData.sales_channels_other}
                      onChange={(e) => setFormData({...formData, sales_channels_other: e.target.value})}
                      placeholder="e.g., Local shop, Website"
                      data-testid="channels-other-input"
                    />
                  </div>
                )}
              </div>

              {/* Business Goals */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2">Tell us about your business *</h3>
                <p className="text-sm text-gray-500">
                  Is this full-time or part time? Approximately how many cards per month do you currently sell? Plan to sell? How many cards per month do you expect to grade today, and in the future?
                </p>
                
                <Textarea
                  value={formData.business_goals}
                  onChange={(e) => setFormData({...formData, business_goals: e.target.value})}
                  placeholder="Tell us about your card business, your goals, and your expected monthly grading volume..."
                  rows={5}
                  data-testid="business-goals-textarea"
                />
              </div>

              {/* Terms & Conditions */}
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="terms"
                    checked={formData.agreed_to_terms}
                    onCheckedChange={(checked) => setFormData({...formData, agreed_to_terms: checked})}
                    data-testid="terms-checkbox"
                  />
                  <div className="grid gap-1.5 leading-none">
                    <Label htmlFor="terms" className="cursor-pointer">
                      I agree to the Dealer Terms & Conditions *
                    </Label>
                    <p className="text-sm text-gray-500">
                      By checking this box, you agree to maintain a minimum of 250 cards submitted per month 
                      and comply with all dealer program requirements.{' '}
                      <a href="/dealer-terms" target="_blank" className="text-blue-600 hover:underline">
                        View full terms
                      </a>
                    </p>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <Button 
                  type="submit" 
                  className="w-full" 
                  size="lg"
                  disabled={loading}
                  data-testid="submit-application-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Submit Application
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>
  );
}
